import { Card, Checkbox, Icon, Spinner, Table, TableCell, TableHeading, TextField } from '@nike/eds';
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, Header, useReactTable } from '@tanstack/react-table';
import './ExceptionsDashboard.css';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsExceptionsLoading, selectExceptions, toggleExceptionSelection } from '../../shared/store/reducers/OverviewReducer';
import { Exception } from '../../shared/Types';
import { StoreDispatch } from '../../shared/store/Store';
import { useEffect } from 'react';

export function ExceptionsDashboard() {
    const dispatch: StoreDispatch = useDispatch();
    const data = useSelector(selectExceptions) as Exception[];
    const isLoading = useSelector(selectIsExceptionsLoading) as boolean;

    const onChange = (exception: Exception) => {
        dispatch(toggleExceptionSelection(exception));
    };
    const toggleSelectAll = (selectAll: boolean) => {
        getRowModel().rows.forEach((row) => {
            const exception = row.original;
            if(selectAll && !exception.selected) {
                dispatch(toggleExceptionSelection(exception));
            }
            if(!selectAll && exception.selected) {
                dispatch(toggleExceptionSelection(exception));
            }
        });
    }

    const columnHelper = createColumnHelper<Exception>();
    const columns = [
        columnHelper.display({
            id: 'select_exception',
            header: () => {
                return <Checkbox id="select-all" label="" checked={getRowModel().rows.every(row => row.original.selected)} onChange={e => toggleSelectAll(e.target.checked)}/>;
            },
            cell: (props) =>  {
                const exception = props.row.original;
                return <Checkbox id={props.row.id} label="" value={exception.id} checked={exception.selected} onChange={e => onChange(exception)}/>;
            },
            minSize: 0,
            maxSize: 20,
            enableSorting: false
        }),
        columnHelper.accessor("momentOfLocalClearance", {
            header: "EiDR batch",
            id: "momentOfLocalClearance",
            minSize: 65,
            maxSize: 65,
            enableSorting: true,
            sortingFn: 'datetime'
        }),
        columnHelper.accessor("productCode", {
            header: "Product Code",
            id: "productCode",
            minSize: 80,
            maxSize: 80,
            enableSorting: true
        }),
        columnHelper.accessor("message", {
            header: "Message",
            id: "message",
            minSize: 40,
            maxSize: 250,
            enableSorting: true
        }),
        columnHelper.accessor("createdAt", {
            header: "Created At",
            id: "createdAt",
            minSize: 70,
            maxSize: 70,
            enableSorting: true,
            sortingFn: 'datetime'
        })
    ];
    const setFilter = (value: string | undefined, header: Header<Exception, unknown>) => {
        header.column.setFilterValue(value);
        if (value) {
            window.history.replaceState(null, '', `?${header.column.id}=${value}`);
        }
        else {
            window.history.replaceState(null, '', window.location.pathname);
        }
    }

    const {getHeaderGroups, getRowModel} = useReactTable({
        columns,
        data: data,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel()
    });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        params.forEach((value, key) => {
            const header = getHeaderGroups()[0].headers.find(h => h.column.id === key);
            if (header) {
                header.column.setFilterValue(value);
            }
        });
    }, [getHeaderGroups]);

    return (
        <div>
            <div id="loader" className={!isLoading ? 'hidden' : ''}>
                <Spinner size="large" />
            </div>
            <Card id="message-no-exceptions" className={isLoading || data.length > 0 ? 'hidden' : ''}>
                <p className="text-center">No exceptions available.</p>
            </Card>
            <div id="exceptions-table-container" className={isLoading || data.length === 0 ? 'hidden' : ''}>
            <Table id="exceptions-table">
                <thead>
                {getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header, index) => (
                                <TableHeading
                                    key={header.id}
                                    style={{
                                        minWidth: header.column.columnDef.minSize,
                                        maxWidth: header.column.columnDef.maxSize,
                                        cursor: header.column.getCanSort() ? 'pointer' : 'default'
                                    }}
                                    className={`eds-type--subtitle-1 eds-color--default ${index === 0 ? "table-action-header" : "table-header"}`}
                                    onClick={() => header.column.getCanSort() && header.column.toggleSorting()}
                                >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanSort() && (
                                    <>
                                        {
                                            header.column.getIsSorted() === 'asc' ? (<Icon name="SortAscending"></Icon>) :
                                            header.column.getIsSorted() === 'desc' ? (<Icon name="SortDescending"></Icon>) :
                                            ''
                                        }

                                    </>
                                )}
                                { index !== 0 && 
                                    <TextField
                                        value={header.column.getFilterValue() as string || ''}
                                        onChange={e => {
                                            setFilter(e.target.value || undefined, header);
                                        }}
                                        placeholder={`Search ...`}
                                        id=""
                                        label=""
                                        />
                                }
                            </TableHeading>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map((cell, index) => (
                            <TableCell
                                key={cell.id}
                                className={`eds-type--body-3 ${index === 0 ? "table-action-data" : "table-data"}`}
                                style={{
                                    minWidth: cell.column.columnDef.minSize,
                                    maxWidth: cell.column.columnDef.maxSize
                                }}
                            >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                        ))}
                    </tr>
                ))}
                </tbody>
            </Table>
            </div>
        </div>
    );
}